import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LaunchIcon from '@mui/icons-material/Launch';
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';

import { Lab } from 'api/labs';
import { HighlightedText } from 'components/atoms/HighlightedText';
import { CustomerBucketProvisioningStatus } from './types';

interface ManageLabsListProps {
  canCreateLab: boolean;
  canDeleteLab: boolean;
  canEditLab: boolean;
  emptyStateMessage: string;
  filteredLabs: Lab[];
  isLoading: boolean;
  labSearchText: string;
  onDelete: (lab: Lab) => void;
  onEdit: (labId: string) => void;
  onNavigateToStudies: (labId: string) => void;
  onReprovisionLabCustomerBucket: (labId: string) => void;
  onRestore: (labId: string) => void;
  onCloseProvisioningFeedback: () => void;
  provisioningFeedback: CustomerBucketProvisioningStatus | null;
  reprovisioningLabCustomerBucketId?: string;
  restoringLabId?: string;
}

export const ManageLabsList: React.FC<ManageLabsListProps> = ({
  canCreateLab,
  canDeleteLab,
  canEditLab,
  emptyStateMessage,
  filteredLabs,
  isLoading,
  labSearchText,
  onCloseProvisioningFeedback,
  onDelete,
  onEdit,
  onNavigateToStudies,
  onReprovisionLabCustomerBucket,
  onRestore,
  provisioningFeedback,
  reprovisioningLabCustomerBucketId,
  restoringLabId,
}) => {
  const normalizedSearch = labSearchText.trim();

  return (
    <Grid item>
      {isLoading ? (
        <CircularProgress size={24} />
      ) : filteredLabs.length === 0 ? (
        normalizedSearch ? (
          <Typography color="text.secondary" data-testid="no-labs-match-message">
            No labs matched &ldquo;{labSearchText.trim()}&rdquo;.
          </Typography>
        ) : (
          <Typography color="text.secondary">{emptyStateMessage}</Typography>
        )
      ) : (
        <List disablePadding>
          {filteredLabs.map((lab, index) => {
            const isArchivedLab = Boolean(lab.deletedAt);
            const isCustomerBucketMissing = !lab.customerBucketName;
            const canManageCustomerBucket = canCreateLab && !isArchivedLab;
            const isReprovisioningLabCustomerBucket = reprovisioningLabCustomerBucketId === lab.id;
            const defaultProvisioningMessage = 'Customer bucket provisioning completed successfully.';

            return (
              <React.Fragment key={lab.id}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  disablePadding
                  sx={{ py: 1, flexDirection: 'column', alignItems: 'stretch' }}
                  data-testid={`lab-list-item-${lab.id}`}
                >
                  <Grid container alignItems="center" justifyContent="space-between" spacing={1}>
                    <Grid item xs>
                      <ListItemText
                        primary={<HighlightedText value={lab.name} query={normalizedSearch} />}
                        secondary={
                          <>
                            ID: <HighlightedText value={lab.id} query={normalizedSearch} component="span" />
                          </>
                        }
                      />
                      {lab.customerBucketName ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          data-testid={`customer-bucket-name-${lab.id}`}
                        >
                          Customer bucket: {lab.customerBucketName}
                        </Typography>
                      ) : null}
                      {isArchivedLab ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          data-testid={`archived-lab-metadata-${lab.id}`}
                        >
                          Archived at {lab.deletedAt}
                          {lab.deletedBy ? ` by ${lab.deletedBy}` : ''}
                        </Typography>
                      ) : null}
                      {isCustomerBucketMissing ? (
                        <Grid container spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                          <Grid item>
                            <Chip
                              color="warning"
                              label="Customer bucket provisioning required"
                              size="small"
                              variant="outlined"
                              data-testid={`missing-customer-bucket-chip-${lab.id}`}
                            />
                          </Grid>
                          {canManageCustomerBucket ? (
                            <Grid item>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => onReprovisionLabCustomerBucket(lab.id)}
                                disabled={isReprovisioningLabCustomerBucket}
                                data-testid={`reprovision-customer-bucket-button-${lab.id}`}
                              >
                                {isReprovisioningLabCustomerBucket
                                  ? 'Re-provisioning customer bucket…'
                                  : 'Re-provision customer bucket'}
                              </Button>
                            </Grid>
                          ) : null}
                        </Grid>
                      ) : null}
                      {provisioningFeedback?.labId === lab.id ? (
                        <Alert
                          severity={provisioningFeedback.severity}
                          sx={{ mt: 1 }}
                          data-testid={`provisioning-feedback-${lab.id}`}
                          onClose={onCloseProvisioningFeedback}
                        >
                          {provisioningFeedback.message || defaultProvisioningMessage}
                        </Alert>
                      ) : null}
                    </Grid>
                    <Grid item>
                      <Grid container spacing={0.5} alignItems="center">
                        {!isArchivedLab ? (
                          <Grid item>
                            <Tooltip title="View studies" placement="top" arrow>
                              <IconButton
                                size="small"
                                aria-label={`view studies for ${lab.name}`}
                                data-testid={`view-studies-button-${lab.id}`}
                                onClick={() => onNavigateToStudies(lab.id)}
                              >
                                <LaunchIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        ) : null}
                        {canEditLab && !isArchivedLab ? (
                          <Grid item>
                            <IconButton
                              size="small"
                              aria-label={`edit ${lab.name}`}
                              data-testid={`edit-lab-button-${lab.id}`}
                              onClick={() => onEdit(lab.id)}
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                        ) : null}
                        {canDeleteLab && !isArchivedLab ? (
                          <Grid item>
                            <IconButton
                              size="small"
                              aria-label={`delete ${lab.name}`}
                              data-testid={`delete-lab-button-${lab.id}`}
                              onClick={() => onDelete(lab)}
                            >
                              <DeleteRoundedIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                        ) : null}
                        {canDeleteLab && isArchivedLab ? (
                          <Grid item>
                            <Tooltip title="Restore lab" placement="top" arrow>
                              <IconButton
                                size="small"
                                aria-label={`restore ${lab.name}`}
                                data-testid={`restore-lab-button-${lab.id}`}
                                disabled={restoringLabId === lab.id}
                                onClick={() => onRestore(lab.id)}
                              >
                                <RestoreRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        ) : null}
                      </Grid>
                    </Grid>
                  </Grid>
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      )}
    </Grid>
  );
};
